// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ButtonProps } from "@insite/mobius/Button";
import ButtonPresentationPropsDefault from "@insite/mobius/Button/presentationProps";
import get from "@insite/mobius/utilities/get";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import resolveColor from "@insite/mobius/utilities/resolveColor";
import { constants } from "@rugby/XDL/constants";
import { xBaseTheme } from "@rugby/XDL/types";
import { color, hexToRGB } from "@rugby/XDL/Utilities";
import { css } from "styled-components";
import { applyStyleModifiers } from "styled-components-modifiers";

type BWT = Pick<ButtonProps, "shadow"> &
    InjectableCss & {
        _sizeVariant: ButtonProps["sizeVariant"];
        _size: ButtonProps["size"] | null;
        _color: ButtonProps["color"];
        _shape: ButtonProps["shape"];
    };

const buttonSizeVariants = {
    small: {
        height: 34,
        fontSize: 14,
        padding: "0 11px",
        icon: 13,
        iconPadding: 5,
    },
    medium: {
        height: 38,
        width: 129,
        fontSize: 15,
        padding: "0 36px",
        icon: 15,
        iconPadding: 7,
    },
    large: {
        height: 38,
        width: 129,
        fontSize: 20,
        padding: "0 36px",
        icon: 20,
        iconPadding: 9,
    },
};

const buttonShapes = {
    rounded: () => `
        border-radius: ${constants.button.rounded}px;
    `,
};

export const buttonPresentationStyle = (buttonType: string) => {
    const disabledAlpha = 0.5;
    const buttonTypeColor = buttonType === "tertiary" ? "common.tertiary" : buttonType;
    return {
        ...ButtonPresentationPropsDefault,
        color: buttonTypeColor,
        buttonType: "solid",
        shape: "rounded",
        sizeVariant: "medium",
        mergeCss: true,
        css: css`
            &:hover {
                background: ${getColor("text.main")};
                border-color: ${getColor("text.main")};
                ${(props: any) =>
                    props.buttonType === "outline" &&
                    css`
                        &&& {
                            color: ${getColor("common.white")};
                        }
                    `}
            }

            &:focus {
                outline: none;
                box-shadow: 0 0 8px 0 ${getColor(buttonTypeColor)};
            }

            &:disabled {
                background: ${({ theme }) => hexToRGB(resolveColor(buttonTypeColor, theme), disabledAlpha)};
                border: 0;
            }

            height: auto;
            min-height: ${(props: BWT) =>
                props["_size"] ||
                get(buttonSizeVariants, [props["_sizeVariant"], "height"]) ||
                buttonSizeVariants.medium.height}px;

            padding: ${({ _sizeVariant }) => get(buttonSizeVariants, [_sizeVariant, "padding"]) || "0 1em"};

            > * {
                font-size: ${(props: BWT) => get(buttonSizeVariants, [props["_sizeVariant"], "fontSize"])}px;
            }

            ${(props: any) => props.buttonType !== "outline" && `text-shadow: 1px 1px 1px rgba(0, 77, 110, 0.45)`}
            font-weight: bold;
            letter-spacing: 0;
            line-height: 2;
        `,
    };
};

export default {
    primary: buttonPresentationStyle("primary"),
    secondary: buttonPresentationStyle("secondary"),
    tertiary: {
        ...buttonPresentationStyle("tertiary"),
        css: css`
                ${buttonPresentationStyle("tertiary").css}
                color: ${getColor("text.main")};
                
                text-shadow: none;
                background: transparent;
                border: 1px solid ${color("common.tertiary", 0.6)};
               
                div[class^="IconWrapper-"] {
                    color: ${getColor("text.main")};
                }

                &:hover {
                    color: ${getColor("common.white")};

                  div[class^="IconWrapper-"] {
                        color: ${getColor("common.white")};
                    }
                }
            `,
    },
} as xBaseTheme["button"];
